<template>
    <div class="flex flex-col gap-4 rounded-lgr bg-surface p-3 fs-mask md:p-4">
        <div class="grid min-h-32 grid-cols-7 items-end gap-1 md:min-h-36">
            <div v-for="(bin, index) in bins" :key="bin" class="flex min-w-0 flex-col items-center gap-2">
                <BaseTooltip
                    v-for="person in people.filter((person) => person.score === bin)"
                    :key="person.id"
                    :trigger-aria-label="person.label"
                    :as-child="false"
                    open-on-click
                    content-class="fs-mask">
                    <template #trigger>
                        <span
                            class="block rounded-full"
                            :class="person.id === viewerId && ['ring-1 ring-offset-2 ring-offset-surface', { 'ring-ink': !person.fill }]"
                            :style="person.id === viewerId && person.fill ? { '--tw-ring-color': person.fill } : undefined">
                            <PerspectiveAvatar :name="person.name" :letters="person.letters" :size="isDesktop ? 'sm' : '2xs'" aria-hidden="true" />
                        </span>
                    </template>
                    <template #content>{{ person.label }}</template>
                </BaseTooltip>
                <span
                    class="mt-2 h-2 w-full"
                    :class="[
                        barFill(bin) === undefined ? 'bg-border' : barFill(bin) ? '' : 'bg-ink-muted',
                        { 'rounded-l-full': index === 0, 'rounded-r-full': index === bins.length - 1 },
                    ]"
                    :style="barFill(bin) ? { backgroundColor: barFill(bin)! } : undefined"
                    aria-hidden="true" />
            </div>
        </div>
        <div class="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2">
            <span class="scaffold-details-small text-ink md:scaffold-body-small">{{ leftLabel }}</span>
            <span class="scaffold-details-small text-center text-ink md:scaffold-body-small">{{ t("teamPerspective.spread.balanced") }}</span>
            <span class="scaffold-details-small text-right text-ink md:scaffold-body-small">{{ rightLabel }}</span>
        </div>
        <ul class="sr-only">
            <li v-for="person in people" :key="person.id">
                <span role="meter" :aria-label="person.name" :aria-valuemin="-3" :aria-valuemax="3" :aria-valuenow="person.score" :aria-valuetext="preference(person.score)">{{
                    person.label
                }}</span>
            </li>
        </ul>
    </div>
</template>
<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import type { TeamPerspectiveMember, TeamScore } from "~vue/api/generated/team-perspective";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import { getPerspectiveSwatch } from "~vue/utils/perspectivePalette";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ scores: TeamScore[]; members: TeamPerspectiveMember[]; leftLabel: string; rightLabel: string; viewerId: string }>();
const { t } = useI18n();
const isDesktop = useBreakpoints(breakpointsTailwind).greaterOrEqual("md");
const bins = [-3, -2, -1, 0, 1, 2, 3];
const people = computed(() => {
    const members = new Map(props.members.map((member) => [member.id, member]));
    return props.scores.map((score) => {
        const member = members.get(score.member_id);
        const name = member?.name ?? "";
        return {
            id: score.member_id,
            name,
            letters: member?.result?.letters,
            fill: getPerspectiveSwatch(member?.result?.letters)?.fill,
            score: score.score,
            label: t("perspective.team.memberWithDetail", {
                name: score.member_id === props.viewerId ? t("teamPage.youName", { name }) : name,
                detail: preference(score.score),
            }),
        };
    });
});
/** The avatar stacked nearest the bar colours it; `null` means occupied but unswatched, `undefined` means empty. */
function barFill(bin: number): string | null | undefined {
    // A backward loop rather than findLast/toReversed, which the iOS 15.0-15.3 webviews the app still supports lack.
    for (let index = people.value.length - 1; index >= 0; index--) {
        const person = people.value[index];
        if (person.score === bin) {
            return person.fill ?? null;
        }
    }
    return undefined;
}
function preference(score: number) {
    const side = score < 0 ? props.leftLabel : props.rightLabel;
    if (score === 0) {
        return t("teamPerspective.spread.balanced");
    }
    if (Math.abs(score) === 1) {
        return t("teamPerspective.preference.slight", { side });
    }
    if (Math.abs(score) === 2) {
        return t("teamPerspective.preference.moderate", { side });
    }
    return t("teamPerspective.preference.strong", { side });
}
</script>
